import {
  EventEditModes,
  FolioDocumentEventTimes,
  FolioDocumentTypeGroup,
  ReceiptEvent,
  TypeGroupOpenModes,
} from "@/types/folio/folio-document"
import { ItineraryEventFolioDefaultTrip } from "@/types/itinerary"
import { TripRef } from "@/types/trip"
import {
  CaseReducer,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"

import { RootState } from "./redux-store"
import { selectTripRef } from "./trip-slice"

export interface FolioDocumentSliceState {
  eventEditMode: EventEditModes
  itineraryEvent: ReceiptEvent | undefined
  defaultTrip: ItineraryEventFolioDefaultTrip | undefined
  times: FolioDocumentEventTimes
  uploadingFolioDocumentMap: { [key: TripRef]: number[] }
  assignedTripId: number | undefined // For folio page trip assignment
  typeGroupOpenMode: TypeGroupOpenModes | undefined
  typeGroup: FolioDocumentTypeGroup | undefined
  assigningTrip: boolean
}

export interface UploadingFolioDocument {
  tripRef: TripRef
  folioDocumentId: number
}

export const folioDocumentSliceInitialState: FolioDocumentSliceState = {
  eventEditMode: "closed",
  itineraryEvent: undefined,
  defaultTrip: undefined,
  typeGroupOpenMode: undefined,
  typeGroup: undefined,
  times: {
    startAt: undefined,
    endAt: undefined,
    timeZone: undefined,
    endTimeZone: undefined,
    type: undefined,
  },
  uploadingFolioDocumentMap: {},
  assignedTripId: undefined,
  assigningTrip: false,
}

export const selectFolioDocumentState = (state: RootState) =>
  state.folioDocumentSlice

export const selectIsTypeGroupOpen = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) =>
    folioDocumentSliceState.typeGroupOpenMode === "opened"
)

export const selectTypeGroup = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) => folioDocumentSliceState.typeGroup
)

export const selectDefaultTrip = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) => folioDocumentSliceState.defaultTrip
)

export const selectIsEventEditOpen = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) =>
    folioDocumentSliceState.eventEditMode === "opened"
)

export const selectItineraryEvent = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) => folioDocumentSliceState.itineraryEvent
)

export const selectFolioDocumentEventTimes = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) => folioDocumentSliceState.times
)

export const selectNumberUploadingFolioDocuments = createSelector(
  [selectFolioDocumentState, selectTripRef],
  (folioDocumentSliceState, tripRef) => {
    let array: number[] | undefined
    if (tripRef) {
      array = folioDocumentSliceState.uploadingFolioDocumentMap[tripRef]
    }
    return array ? array.length : 0
  }
)

export const selectIsAssigningTrip = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) => folioDocumentSliceState.assigningTrip
)

const setTypeGroupOpenMode: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<EventEditModes>
> = (state, action) => {
  state.typeGroupOpenMode = action.payload
}

const setTypeGroup: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<FolioDocumentTypeGroup>
> = (state, action) => {
  state.typeGroup = action.payload
  if (action.payload.events.length === 1) {
    state.itineraryEvent = action.payload.events[0]
  }
}

export const selectAssignedTripId = createSelector(
  [selectFolioDocumentState],
  (folioDocumentSliceState) => {
    return folioDocumentSliceState.assignedTripId
  }
)

const setEventEditMode: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<EventEditModes>
> = (state, action) => {
  state.eventEditMode = action.payload
}

const setItineraryEvent: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<ReceiptEvent | undefined>
> = (state, action) => {
  state.itineraryEvent = action.payload
}

const setDefaultTrip: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<ItineraryEventFolioDefaultTrip | undefined>
> = (state, action) => {
  state.defaultTrip = action.payload
}

const setFolioDocumentEventTimes: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<FolioDocumentEventTimes>
> = (state, action) => {
  state.times = action.payload
}

const addUploadingFolioDocument: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<UploadingFolioDocument>
> = (state, action) => {
  const array: number[] | undefined =
    state.uploadingFolioDocumentMap[action.payload.tripRef]
  if (!array) {
    state.uploadingFolioDocumentMap[action.payload.tripRef] = [
      action.payload.folioDocumentId,
    ]
  } else {
    if (array.indexOf(action.payload.folioDocumentId) === -1)
      array.push(action.payload.folioDocumentId)
  }
}

const removeUploadingFolioDocument: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<UploadingFolioDocument>
> = (state, action) => {
  const array: number[] | undefined =
    state.uploadingFolioDocumentMap[action.payload.tripRef]
  if (array) {
    const index = array.indexOf(action.payload.folioDocumentId)
    if (index !== -1) {
      array.splice(index, 1)
    }
  }
}

const setAssignedTripId: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<number>
> = (state, action) => {
  state.assignedTripId = action.payload
}

const setIsAssigningTrip: CaseReducer<
  FolioDocumentSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.assigningTrip = action.payload
}

const folioDocumentSlice = createSlice({
  name: "folio-document",
  initialState: folioDocumentSliceInitialState,
  reducers: {
    reset: () => folioDocumentSliceInitialState,
    setEventEditMode,
    setItineraryEvent,
    setDefaultTrip,
    setFolioDocumentEventTimes,
    addUploadingFolioDocument,
    removeUploadingFolioDocument,
    setAssignedTripId,
    setTypeGroupOpenMode,
    setTypeGroup,
    setIsAssigningTrip,
  },
})

export type folioDocumentSlice = typeof folioDocumentSlice

export default folioDocumentSlice
